import { format, compareAsc, compareDesc, parseISO } from 'date-fns'
import { LocalisationContextValue } from '../context/localisation/localisation.types'
import { getDateFnsLocaleByLocaleString } from './getDateFnsLocaleByLocaleString'
import { PlatformError } from './errors'

const ONE_SECOND = 1000
const ONE_MINUTE = ONE_SECOND * 60
export const ONE_HOUR = ONE_MINUTE * 60
export const ONE_DAY = ONE_HOUR * 24
export const tomorrowDate = () => new Date(Date.now() + ONE_DAY)

const DEFAULT_DATE_TIME_FORMAT = 'do MMMM, h:mma'

const LOCALISED_DATE_FORMAT = 'eee, MMM dd, yyyy'
const LOCALISED_DATE_TIME_FORMAT_12HR = 'eee, MMM dd, yyyy, hh:mm a'
const LOCALISED_DATE_TIME_FORMAT_24HR = 'eee, MMM dd, yyyy, HH:mm'
const LOCALISED_TIME_FORMAT = 'hh:mm a'

const FLIGHT_DATE_FORMAT = 'dd MMMM yyyy'

type DateString = Date | string | undefined
type DateFormatterFunc<D = DateString> = <T extends D>(
  rawDate?: T,
  dateFormat?: string,
  locale?: string
) => T extends undefined ? null : string
type LocalDateFormatterFunc<T = Date | string> = (
  date: T,
  localisation: LocalisationContextValue
) => string

export const formatDate = ((rawDate, dateFormat, locale) => {
  if (!rawDate) {
    return null
  }
  const formatString = dateFormat ?? DEFAULT_DATE_TIME_FORMAT
  const options = {
    locale: getDateFnsLocaleByLocaleString(locale),
  }
  return typeof rawDate === 'string'
    ? format(new Date(rawDate), formatString, options)
    : format(rawDate, formatString, options)
}) as DateFormatterFunc

const formatLocalDate = ((rawDate, dateFormat, locale) => {
  if (!rawDate) {
    return null
  }
  return format(rawDate, dateFormat ?? DEFAULT_DATE_TIME_FORMAT, {
    locale: getDateFnsLocaleByLocaleString(locale),
  })
}) as DateFormatterFunc<Date>

export const formatLocalISODateString = ((rawDate, dateFormat, locale) => {
  if (!rawDate) {
    return null
  }
  const date = parseISO(rawDate)
  return formatLocalDate(date, dateFormat, locale)
}) as DateFormatterFunc<string>

const createLocalFormatterFunc =
  (dateFormat: string): LocalDateFormatterFunc =>
  (date, { locale }) =>
    typeof date === 'string'
      ? formatLocalISODateString(date, dateFormat, locale)
      : formatLocalDate(date, dateFormat, locale)

export const formatLocalisedLocalDate = createLocalFormatterFunc(LOCALISED_DATE_FORMAT)
export const formatLocalisedLocalDateTime = createLocalFormatterFunc(
  LOCALISED_DATE_TIME_FORMAT_12HR
)
export const formatLocalisedLocalDateTime24hr = createLocalFormatterFunc(
  LOCALISED_DATE_TIME_FORMAT_24HR
)
export const formatLocalisedLocalTime = createLocalFormatterFunc(LOCALISED_TIME_FORMAT)

export const formatLocalisedDateShort: LocalDateFormatterFunc<Date> = (date, { locale }) =>
  date.toLocaleDateString(locale, {
    weekday: 'short',
    month: 'short',
    day: 'numeric',
  })

export const formatFlightDate = (rawDate: string) => formatDate(rawDate, FLIGHT_DATE_FORMAT)

export interface Duration {
  days?: number
  hours?: number
  minutes?: number
  seconds?: number
}

const splitAmountToUnits = (amount: number, units: number[]) => {
  const { acc: splitted } = units
    .slice()
    .sort((a, b) => b - a)
    .reduce(
      ({ acc, amountLeft }, unit) => {
        return {
          acc: [...acc, Math.floor(amountLeft / unit)],
          amountLeft: amountLeft % unit,
        }
      },
      {
        acc: new Array<number>(),
        amountLeft: amount,
      }
    )

  return splitted
}

export const minutesToDuration = (minutes: number): Duration => {
  if (minutes <= 0) {
    return {
      days: 0,
      hours: 0,
      minutes: 0,
      seconds: 0,
    }
  }

  const min = 1
  const hourInMin = 60 * min
  const dayInMin = 24 * hourInMin

  const [days, hours, mins] = splitAmountToUnits(minutes, [dayInMin, hourInMin, min])

  return {
    days,
    hours,
    minutes: mins,
    seconds: 0,
  }
}

// Compares min and max duration if only minutes or hours returns number, else returns duration object
export const compareMinMaxDuration = (minDuration: Duration, maxDuration: Duration) => {
  if (
    minDuration.hours !== 0 &&
    maxDuration.hours !== 0 &&
    minDuration.minutes !== 0 &&
    maxDuration.minutes !== 0
  ) {
    return minDuration
  }
  if (
    minDuration.hours !== 0 &&
    maxDuration.hours !== 0 &&
    minDuration.minutes === 0 &&
    maxDuration.minutes === 0 &&
    minDuration.hours !== undefined
  ) {
    return minDuration.hours
  }
  if (
    minDuration.hours === 0 &&
    maxDuration.hours === 0 &&
    minDuration.minutes !== 0 &&
    maxDuration.minutes !== 0 &&
    minDuration.minutes !== undefined
  ) {
    return minDuration.minutes
  }
  return minDuration
}

export const formatMinutesToHours = (minutes: number): string => {
  const hours = Math.floor(minutes / 60)
  const mins = minutes % 60
  if (hours === 0) {
    return `${mins}m`
  }
  if (mins === 0) {
    return `${hours}h`
  }
  return `${hours}h ${mins}m`
}

export const compareDates = (date1?: Date, date2?: Date, order?: 'ASC' | 'DESC'): number => {
  const isDesc = order === 'DESC'
  if (!date1 && !date2) {
    return 0
  }
  if (!date1) {
    return isDesc ? 1 : -1
  }
  if (!date2) {
    return isDesc ? -1 : 1
  }

  return isDesc ? compareDesc(date1, date2) : compareAsc(date1, date2)
}

export const preserveDateWhenConvertedToUTC = (localDate: Date): Date => {
  const localDay = localDate.getDate()
  const utcDay = new Date(localDate.toISOString()).getUTCDate()

  if (localDay === utcDay) {
    return localDate
  }

  const offsetHours = -localDate.getTimezoneOffset() / 60

  let safeHour: number

  if (offsetHours > 0) {
    safeHour = Math.ceil(offsetHours)
  } else {
    safeHour = 24 + Math.floor(offsetHours) - 1
  }

  return new Date(
    localDate.getFullYear(),
    localDate.getMonth(),
    localDate.getDate(),
    safeHour,
    0,
    0,
    0
  )
}

export const setTimeToDate = (startDate: string, startTime?: string) => {
  if (!startTime) {
    return new Date(startDate)
  }

  const parsedDate = parseISO(startDate)
  const [hours, minutes] = startTime.split(':').map((x) => parseInt(x, 10))

  const dateTime = new Date(parsedDate)
  dateTime.setHours(hours)
  dateTime.setMinutes(minutes)

  return dateTime
}

// Adjust datetime if start date is beyond a threshold in hours, set time to user time
export const adjustDateObjectTimeToUserTime = (
  selectedDate: Date,
  thresholdInHours: number,
  minDate: Date
): Date => {
  const currentDate = new Date(Date.now())

  if (!Number.isInteger(thresholdInHours) || thresholdInHours < 1) {
    throw new PlatformError('thresholdInHours should be positive integer')
  }

  // Create a new Date object with adjusted time for threshold
  const threshold = new Date(currentDate.getTime() + thresholdInHours * 60 * 60 * 1000)

  let adjustedDate = selectedDate

  // Check if time adjustment is needed
  if (selectedDate > threshold) {
    const year = selectedDate.getFullYear()
    const month = selectedDate.getMonth()
    const day = selectedDate.getDate()
    const hours = currentDate.getHours()
    const minutes = currentDate.getMinutes()
    const seconds = currentDate.getSeconds()
    const milliseconds = currentDate.getMilliseconds()

    // Create a new Date object with selected date and current time components
    adjustedDate = new Date(year, month, day, hours, minutes, seconds, milliseconds)
  }

  // Check adjusted date is after minDate
  return adjustedDate > minDate ? adjustedDate : minDate
}
